import { GridsterItemComponentInterface } from "angular-gridster2";
import { Observable, Observer, Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { Group } from "src/AngularJS/Models/group.model";
import { DashboardService } from "src/app/dashboard/dashboard.service";
import type { ResponsiveDashboardComponent } from "src/app/dashboard/responsive-dashboard/responsive-dashboard.component";
import { ModuleContainerComponent } from "../../app/dashboard/module-container/module-container.component";
import { DashboardRecipientGroup, DashboardRecipientLogin, DashboardRecipientProfileApiModel } from "./dashboard.model";
import { CopyModule, DashboardModuleEagerLoad, DashboardModuleMap, DashboardModuleResponsiveInstance, DashboardModuleResponsiveInstanceForGridster, ModuleInfo } from "./module-info.model";
import { ModuleManager } from "./module-manager";
import { ModuleManagerTransferData } from "./module-manager/module-manager-transfer-data.model";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";


type GridsterEvent = {
    item: DashboardModuleResponsiveInstanceForGridster;
    component: GridsterItemComponentInterface;
    event?: MouseEvent;
};

export class DashboardManager {
    moduleResizeStarted = new Subject<GridsterEvent>();
    moduleResizeStopped = new Subject<GridsterEvent>();
    moduleResizing = new Subject<GridsterEvent>();
    moduleDragStarted = new Subject<GridsterEvent>();
    moduleDragStopped = new Subject<GridsterEvent>();
    moduleFullscreenToggled = new Subject<boolean>();
    settingsModalReference: NgbActiveModal;
    get dashboardKey() { return this.component.dashboardKey; }
    get isActive() { return this.component.isActive; }
    get isShared() { return this.dashboard?.RelatedDashboardID > 0; }
    get isTemporary() { return this.dashboard?.TemporaryDashboard; }
    get timeZone() { return this.dashboard.TimeZoneInfoName; }
    get currentSharedDashboardOwner() { return this.dashboard.sharedOwner; }
    get writeAccess() {
        if (window.location.pathname.includes('SharedDashboard.aspx')) return false;
        else if ((this.isCXOne && this.service.permissions?.["launch_cxone_dash"]) || (!this.isCXOne && this.service.permissions?.["DashboardNew-Read"]))
            return (!this.isShared || this.dashboard?.writeAccess) && !this.service.isDashboardProjection && !this.service.isDashboardWindow;
        else return false;
    }
    get readGroupAccess() {
        if (window.location.pathname.includes('SharedDashboard.aspx')) return false;
        else return (!this.isShared || this.dashboard.readGroupAccess) && !this.service.isDashboardProjection;
    }
    get isCXOne() { return this.dashboard?.IsCXOne ?? false; }
    get isDashboardProjection() { return this.service.isDashboardProjection; }
    get isDashboardWindow() { return this.service.isDashboardWindow; }
    get agentStateDetailsAccess() {
        if (window.location.pathname.includes('SharedDashboard.aspx')) return false;
        else return this.service.permissions?.["AgentZoomSecurity"];
    }
    get cXOneCoachingManagerAccess() { return this.service.permissions?.["CXOneCoachingManager"]; }
    get giveAwardManagerAccess() { return this.service.permissions?.["GiveAwardSecurity"]; }
    get suggestCoachingAccess() { return this.service.permissions?.["SuggestCoaching"]; }
    get IsReport() { return this.dashboard?.ReportTemplatePermission != null; } // true for prebuilt and custom reports.
    get IsCustomReport() { return this.dashboard?.IsCustomReport ?? false; }
    get isPrebuiltReport() { return this.component?.isPrebuiltReport; }
    get forceLogoutAccess() { return this.service.permissions?.["CXOneForceLogoutAccess"]; }
    get terminateAccess() { return this.service.permissions?.["CXOneTerminateAccess"]; }
    get IsIdle() { return this.component.isIdle; }
    get CanReset() { return this.dashboard?.canReset ?? false; }

    private get dashboard() { return this.component.dashboard; }
    private modulesPreventingMapUpdate = new Set<string>();
    private _previousDashboardMaps: DashboardModuleMap[] = null;
    private _updateModuleMapsDebounce: Observer<void>;
    private _updateModuleMapsObservable: Observable<void>;
    private _fullscreenScrollTop = 0;

    constructor(private component: ResponsiveDashboardComponent, private service: DashboardService) {

    }

    addCopiedModule(copyModule: CopyModule) {
        const module = DashboardModuleResponsiveInstanceForGridster.fromDashboardModuleResponsiveInstance(copyModule.copiedModule);
        this.component.modules.push(module);
        this.component.eagerLoadModules[copyModule.copiedModule.moduleKey] = copyModule.copiedSettings;
    }

    updateEagerLoad(moduleKey: string, eagerLoadedData: DashboardModuleEagerLoad) {
        this.component.eagerLoadModules[moduleKey] = eagerLoadedData;
    }

    async moveToTaskbar(transferData: ModuleManagerTransferData) {
        this.component.service.moduleTransferData[transferData.moduleKey] = transferData;

        let module: DashboardModuleResponsiveInstance;
        for (let i = 0; i < this.component.modules.length; i++) {
            module = this.component.modules[i];
            if (module.moduleKey === transferData.moduleKey) {
                this.component.modules.splice(i, 1);
                this.component.gridster.updateGrid();
                break;
            } else {
                module = null;
            }
        }

        if (module != null) {
            await this.component.service.moveDashboardModule(
                transferData.moduleKey,
                this.dashboardKey,
                this.component.service.taskbarKey
            );

            module.CustomName = transferData.moduleInfo.CustomName;
            this.component.service.taskbarModules.push(module);
            this.updateModuleMaps();
        }
    }

    enterModuleFullscreen() {
        const gridBackgroundEl = this.component.gridBackgroundEl.nativeElement;
        gridBackgroundEl.classList.add('overflow-visible'); //Added to fix issue where the graphs are getting cut off in expanded view.
        this._fullscreenScrollTop = gridBackgroundEl.scrollTop;
        gridBackgroundEl.scrollTop = 0;
        if (this.isCXOne && !this.isDashboardProjection) {
            const dashboardHeaderEl = this.component.dashboardHeaderEl.nativeElement;
            dashboardHeaderEl.classList.add('d-none');
        }
    }

    exitModuleFullscreen() {
        const gridBackgroundEl = this.component.gridBackgroundEl.nativeElement;
        gridBackgroundEl.classList.remove('overflow-visible');
        requestAnimationFrame(() => gridBackgroundEl.scrollTop = this._fullscreenScrollTop);
        if (this.isCXOne && !this.isDashboardProjection) {
            const dashboardHeaderEl = this.component.dashboardHeaderEl.nativeElement;
            dashboardHeaderEl.classList.remove('d-none');
        }
    }

    getGridsterItem(module: DashboardModuleResponsiveInstanceForGridster) {
        return this.component.gridster.getItemComponent(module);
    }

    getModuleById(moduleId: number): ModuleInfo {
        for (const module of this.component.availableModules) {
            if (moduleId === module.ModuleID) {
                return JSON.parse(JSON.stringify(module));
            }
        }

        for (const module of this.component.blockedModules) {
            if (moduleId === module.ModuleID)
                return null;
        }

        return undefined;
    }

    getModuleKeys() {
        return this.component.modules.map(m => m.moduleKey);
    }

    getModuleManager(moduleKey: string): ModuleManager {
        return this.component.moduleContainerList.find(m => m.moduleKey === moduleKey)?.moduleManager;
    }

    getModuleContainerbyModuleKey(moduleKey: string): ModuleContainerComponent {
        return this.component.moduleContainerList.find(m => m.moduleKey === moduleKey);
    }

    getEagerLoadedModule(moduleKey: string) {
        return this.component.eagerLoadModules[moduleKey];
    }

    setModuleKey(moduleKey: string, moduleId: number) {
        for (const module of this.component.modules) {
            if (module.ModuleID === moduleId && module.moduleKey == null) {
                module.moduleKey = moduleKey;
                break;
            }
        }
    }

    cancelAdd(moduleId: number) {
        for (let i = 0; i < this.component.modules.length; i++) {
            const module = this.component.modules[i];
            if (module.ModuleID === moduleId && module.moduleKey == null) {
                this.component.modules.splice(i, 1);
                break;
            }
        }
    }

    async deleteModule(moduleKey: string) {
        for (let i = 0; i < this.component.modules.length; i++) {
            const module = this.component.modules[i];
            if (module.moduleKey === moduleKey) {
                this.component.modules.splice(i, 1);
                this.component.modules = this.component.modules.slice();
                break;
            }
        }

        await this.component.service.removeDashboardModule(moduleKey, this.dashboardKey);
        this.updateModuleMaps();
    }

    preventModuleMapUpdate(moduleKey: string) {
        this.modulesPreventingMapUpdate.add(moduleKey);
    }

    removeModuleMapUpdatePrevention(moduleKey: string) {
        this.modulesPreventingMapUpdate.delete(moduleKey);
    }

    resetModuleMapUpdatePrevention() {
        this.modulesPreventingMapUpdate.clear();
    }

    setInitialMaps(maps: DashboardModuleMap[]) {
        this._previousDashboardMaps = maps;
    }

    async updateModuleMaps() {
        if (this._updateModuleMapsDebounce == null) {
            this._updateModuleMapsObservable = new Observable<void>(o => this._updateModuleMapsDebounce = o)
                .pipe(debounceTime(500));

            this._updateModuleMapsObservable.subscribe(async () => await this.updateModuleMapsDebounced());
        }

        this._updateModuleMapsDebounce.next();
    }

    requestPublishNeeded() {
        if (this.isDashboardProjection || this.isDashboardWindow)
            return;

        if (!this.isCXOne) {
            // If dashboard is not shared, do not need to publish
            if (!this.isShared && !this.dashboard.sharedOwner)
                return;

            // If dashboard does not have write access and the user does not own the dashboard, they can not publish
            if (!this.writeAccess && !this.dashboard.sharedOwner)
                return;

            this.component.dashboardInfo.NeedConfirmationToClose = true;

            if (this.dashboard.readControlAccess && !this.dashboard.sharedOwner)
                return;
        }

        if (this.isCXOne && this.isPrebuiltReport) {
            this.component.publishChanges();
            this.dashboard.canReset = true;
            return;
        }

        this.component.dashboardInfo.NeedConfirmationToClose = true;
        this.component.publishPending = true;
        this.component.dashboardInfo.publishPending = true;
        this.requestEditNeeded();
    }

    requestSaveTemporaryDashboardNeeded() {
        if (this.isTemporary && this.dashboard.sharedOwner)
            this.component.temporaryDashboardPending = true;
    }

    requestEditNeeded() {
        // For CXOne dashboard ask the user to save changes on their owned dashboards
        this.component.editPending = true;
        if (this.isCXOne)
            sendMessageCXOne({ hasNonSavedChanges: true });
    }

    reloadDashboard(changes: any = null) {
        this.component.initDashboard();

        const availableDashboard = this.service.availableDashboards.find(d => d.DashboardKey === this.component.dashboardKey);
        changes?.forEach(c => {
            if(c.isLogin)
            {
                const recipientLogins = availableDashboard.RecipientLogins;
                const loginToChange = recipientLogins?.find(l => l.LoginID === c.id);
                if(loginToChange)
                {
                    if(c.permission === "None")
                    {
                        const i = recipientLogins.indexOf(loginToChange);
                        recipientLogins.splice(i, 1);
                        return;
                    }
                    loginToChange.read = c.permission === "Read";
                    loginToChange.readTheirs = c.permission === "ReadTheirs";
                    loginToChange.readGroup = c.permission === "ReadGroup";
                    loginToChange.readControl = c.permission === "ReadControl";
                    loginToChange.write = c.permission === "Write";
                    loginToChange.writeTheirs = c.permission === "WriteTheirs";
                    loginToChange.template = c.permission === "Template";
                }
                else
                {
                    if (!c.newEntry?.login) {
                        return;
                    }
                    const newLogin: DashboardRecipientLogin = {
                        LoginID: c.newEntry?.login?.LoginID,
                        CompanyID: c.newEntry?.login?.CompanyID,
                        LoginName: c.newEntry?.login?.LoginName,
                        FirstName: c.newEntry?.login?.FirstName,
                        LastName: c.newEntry?.login?.LastName,
                        FullName: c.newEntry?.login?.FullName,
                        GroupID: c.newEntry?.login?.GroupID,
                        RelatedGroupLevel: c.newEntry?.login?.RelatedGroupLevel,
                        RelatedGroupType: c.newEntry?.login?.RelatedGroupType,
                        ProfileID: c.newEntry?.login?.ProfileID,
                        Active: c.newEntry?.login?.Active,
                        read: c.permission === "Read",
                        readTheirs: c.permission === "ReadTheirs",
                        readGroup: c.permission === "ReadGroup",
                        readControl: c.permission === "ReadControl",
                        write: c.permission === "Write",
                        writeTheirs: c.permission === "WriteTheirs",
                        template: c.permission === "Template"
                    };

                    const newRecipientsLogins: DashboardRecipientLogin[] = [];
                    newRecipientsLogins.push(newLogin);

                    availableDashboard.RecipientLogins = newRecipientsLogins;
                }
            }
            else
            {
                const recipientGroups = availableDashboard?.RecipientGroups;
                const groupToChange = recipientGroups ? recipientGroups[c.groupID] : null;
                if(groupToChange)
                {
                    if(c.permission === "None")
                    {
                        if(recipientGroups[c.groupID].Profiles.length <= 1) // if only one profile has a permission, just remove the Group
                            delete recipientGroups[c.groupID];
                        else
                        {
                            const profileToDelete = recipientGroups[c.groupID].Profiles.find(p => p.ProfileID === c.id);
                            const i = recipientGroups[c.groupID].Profiles.indexOf(profileToDelete);
                            recipientGroups[c.groupID].Profiles.splice(i, 1);
                        }
                        return;
                    }
                    groupToChange.Profiles.forEach(profile => {
                        profile.read = c.permission === "Read";
                        profile.readTheirs = c.permission === "ReadTheirs";
                        profile.readGroup = c.permission === "ReadGroup";
                        profile.readControl = c.permission === "ReadControl";
                        profile.write = c.permission === "Write";
                        profile.writeTheirs = c.permission === "WriteTheirs";
                        profile.template = c.permission === "Template";
                    });
                }
                else
                {
                    const group: Group = {
                        ACDID: c.newEntry?.group?.ACDID,
                        ACDSource: c.newEntry?.group?.ACDSource,
                        Active: c.newEntry?.group?.Active,
                        BaseGroupType: c.newEntry?.group?.BaseGroupType,
                        CompanyID: c.newEntry?.group?.CompanyID,
                        Email: c.newEntry?.group?.Email,
                        FirstName: c.newEntry?.group?.FirstName,
                        Group1ID: c.newEntry?.group?.Group1ID,
                        Group2ID: c.newEntry?.group?.Group2ID,
                        Group3ID: c.newEntry?.group?.Group3ID,
                        Group4ID: c.newEntry?.group?.Group4ID,
                        Group5ID: c.newEntry?.group?.Group5ID,
                        GroupID: c.newEntry?.group?.GroupID,
                        GroupLevel: c.newEntry?.group?.GroupLevel,
                        GroupName: c.newEntry?.group?.GroupName,
                        LastName: c.newEntry?.group?.LastName,
                        ParentGroupID: c.newEntry?.group?.ParentGroupID,
                        ReceiveAlerts: c.newEntry?.group?.ReceiveAlerts,
                        ImageID: c.newEntry?.group?.ImageID
                    };

                    const profiles: DashboardRecipientProfileApiModel[] = [];

                    c.newEntry?.profiles?.forEach((value) => {
                        const profile: DashboardRecipientProfileApiModel =
                        {
                            relatedLogins: [],
                            ProfileID: value.profile.ProfileID,
                            CompanyID: value.profile.CompanyID,
                            ProfileName: value.profile.ProfileName,
                            GroupID: value.profile.GroupID,
                            GroupLevel: value.profile.GroupLevel,
                            GroupType: value.profile.GroupType,
                            GroupTypeStr: value.profile.GroupTypeStr,
                            read: c.permission === "Read",
                            readTheirs: c.permission === "ReadTheirs",
                            readGroup: c.permission === "ReadGroup",
                            readControl: c.permission === "ReadControl",
                            write: c.permission === "Write",
                            writeTheirs: c.permission === "WriteTheirs",
                            template: c.permission === "Template",
                            relatedLoginIds: []
                        };

                        profiles.push(profile);
                    });

                    const dashboardRecipientGroup: DashboardRecipientGroup =
                    {
                        Group: group,
                        Profiles: profiles
                    };

                    recipientGroups[c.groupID] = dashboardRecipientGroup;

                    availableDashboard.RecipientGroups = recipientGroups;
                }
            }
        });
        this.service.availableDashboardsChanged.emit();
    }

    private async updateModuleMapsDebounced() {
        if (this.modulesPreventingMapUpdate.size === 0) {
            const mapsList = this.component.getModuleMaps();
            // if maps already match, don't save
            if (this._previousDashboardMaps != null && JSON.stringify(this._previousDashboardMaps) === JSON.stringify(mapsList))
                return;

            // save maps
            this._previousDashboardMaps = mapsList;
            await this.component.service.updateAllModuleMaps(this.dashboardKey, mapsList);
            this.requestPublishNeeded();
        } else {
            // queue another save to try again later
            this._updateModuleMapsDebounce.next();
        }
    }
}
